import DeliveryRatesDialog from '../../components/DeliveryRatesDialog';
import { ContactCode } from '../../components/ContactVerification';
import { deliveryAddressText, deliveryAddressLabel } from '../../lib/deliveryAddressText';
import { storeAcceptsPayment } from '../../lib/storePayments';
import { useCart } from '../../context/CartContext';
import { cartLineKey } from '../../lib/cartLineKey';
import useDeliveryQuote from '../../lib/useDeliveryQuote';
import { formatWeight } from '../../components/WeightInfo';
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Store, Bike, CreditCard, LockKeyhole, MapPin, Plus, ShoppingBag } from "lucide-react";
import CustomerAddressDialog from "../../components/CustomerAddressDialog";
import { get, post, put } from "../../lib/api";
import { useAuth } from "../../context/AuthContext"; // To get user ID

const PAYMENT_OPTIONS = [
  { value: "GCASH", label: "GCash" },
  { value: "MAYA", label: "Maya" },
  { value: "CARD", label: "Credit / Debit Card" },
  { value: "SHOPEEPAY", label: "ShopeePay" },
  { value: "QR_PH", label: "QR Ph" },
  { value: "BANK_TRANSFER", label: "Bank transfer" },
  { value: "COD", label: "Cash on Delivery" },
  { value: "COP", label: "Cash On Pick-up" },
];

const Checkout = () => {
  const { user } = useAuth();
  const { ready: cartReady, error: cartSyncError } = useCart();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [checkoutStore, setCheckoutStore] = useState(null);
  const checkoutVendorId = String(cartItems[0]?.vendorId?._id || cartItems[0]?.vendorId || '');
  const addressMatchesStore = address => Boolean(checkoutStore?.branch?.id && String(address?.branch?.id || address?.branchId || '') === checkoutStore.branch.id);
  useEffect(() => {
    let active = true;
    setCheckoutStore(null);
    if (checkoutVendorId) get(`/vendors/${checkoutVendorId}`).then(response => { if (active) setCheckoutStore(response.vendor); }).catch(() => {});
    return () => { active = false; };
  }, [checkoutVendorId]);
  useEffect(() => {
    if (!checkoutStore) return;
    const eligible = addresses.filter(address => String(address.branch?.id || address.branchId || '') === checkoutStore.branch?.id);
    setSelectedAddress(current => eligible.find(address => String(address._id) === String(current?._id)) || eligible.find(address => address.isDefault) || eligible[0] || null);
  }, [addresses, checkoutStore]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState("");
  const location = useLocation();
  const selectedIdsFromNav = location?.state?.selectedIds || null;
  const [paymentMethod, setPaymentMethod] = useState(location?.state?.paymentMethod || "COD");
  const [orderType, setOrderType] = useState(location?.state?.orderType || "DELIVERY");
  const [deliveryNote, setDeliveryNote] = useState(location?.state?.deliveryNote || "");
  const [noteRecipient, setNoteRecipient] = useState(location?.state?.orderType === 'PICKUP' ? 'VENDOR' : location?.state?.noteRecipient || 'DELIVERY');
  const [noteOpen, setNoteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);
  const [pendingOrderIds, setPendingOrderIds] = useState([]);
  const [pendingDeliveryFee, setPendingDeliveryFee] = useState(0);
  const [pendingRoutes, setPendingRoutes] = useState([]);
  const [showRates, setShowRates] = useState(false);
  const [quoteRevision, setQuoteRevision] = useState(0);
  const [profileUser, setProfileUser] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsRevision, setSmsRevision] = useState(0);

  const [systemControls, setSystemControls] = useState(null);
  const providerName = systemControls?.paymentProvider === 'MAYA' ? 'Maya' : 'PayMongo';
  const availableOptions = React.useMemo(() => PAYMENT_OPTIONS.filter((option) =>
    systemControls && systemControls.paymentMethods?.[option.value] !== false &&
    (systemControls.paymentProvider !== 'MAYA' || ['MAYA', 'COD', 'COP'].includes(option.value)) &&
    !(orderType === 'PICKUP' && option.value === 'COD') &&
    !(orderType === 'DELIVERY' && option.value === 'COP') &&
    cartItems.every(item => storeAcceptsPayment(item, option.value))
  ).map((option) => option.value === 'MAYA' && systemControls?.paymentProvider === 'MAYA' ? { ...option, label: 'Maya Checkout' } : option), [systemControls, orderType, cartItems]);
  const currentUser = profileUser || user;
  const canUseCashOnDelivery = currentUser?.verified === true;
  const onlinePayment = !["COD", "COP"].includes(paymentMethod);
  const accountReady = Boolean(canUseCashOnDelivery || (onlinePayment && currentUser?.phone));
  const checkoutLocation = JSON.stringify([orderType, selectedAddress?._id, selectedAddress?.municipality, selectedAddress?.barangay, selectedAddress?.sitio, selectedAddress?.completeAddress, selectedAddress?.location]);
  const verificationReady = accountReady && smsStatus?.context === checkoutLocation && smsStatus.required === false && (orderType !== 'DELIVERY' || addressMatchesStore(selectedAddress));
  useEffect(() => {
    if (!currentUser?.phone || (orderType === "DELIVERY" && !selectedAddress?._id)) { setSmsStatus(null); return; }
    let active = true;
    setSmsStatus(null);
    const params = new URLSearchParams({ orderType, ...(selectedAddress?._id ? { deliveryAddressId: selectedAddress._id } : {}) });
    get("/customers/me/checkout-verification?" + params)
      .then(response => { if (active) setSmsStatus({ ...response, context: checkoutLocation }); })
      .catch(err => { if (active) setSmsStatus({ required: true, reason: err.response?.data?.message || "Unable to check SMS verification. Please try again.", context: checkoutLocation }); });
    return () => { active = false; };
  }, [currentUser?.phone, currentUser?.smsOtpExempt, checkoutLocation, orderType, selectedAddress?._id, smsRevision]);

  useEffect(() => {
    if (systemControls && !pendingOrderIds.length && !availableOptions.some(option => option.value === paymentMethod)) {
      const preferredCash = orderType === 'PICKUP' ? 'COP' : 'COD';
      setPaymentMethod(availableOptions.find(option => option.value === preferredCash)?.value || availableOptions[0]?.value || '');
    }
    if (systemControls?.orderType?.[orderType] === false || !orderType) setOrderType(['DELIVERY', 'PICKUP'].find((value) => systemControls?.orderType?.[value] !== false) || '');
  }, [paymentMethod, canUseCashOnDelivery, systemControls, orderType, pendingOrderIds.length, availableOptions]);

  const paymentOptions = availableOptions.map((option) => ({
    ...option,
    disabled:
      placingOrder ||
      systemControls?.paymentMethods?.[option.value] === false ||
      (orderType === 'PICKUP' && option.value === 'COD') ||
      (orderType === 'DELIVERY' && option.value === 'COP') ||
      (pendingOrderIds.length > 0 && option.value !== paymentMethod),
  }));

  useEffect(() => {
    if (!cartReady || cartSyncError || pendingOrderIds.length) return;
    const fetchCheckoutData = async () => {
      try {
        setLoading(true);
        setError("");

        // Fetch Cart Items
        const cartResponse = await get("/cart");
        if (cartResponse.success && Array.isArray(cartResponse.cart)) {
          let items = cartResponse.cart;
          if (
            selectedIdsFromNav &&
            Array.isArray(selectedIdsFromNav) &&
            selectedIdsFromNav.length > 0
          ) {
            items = items.filter((i) =>
              selectedIdsFromNav.includes(i.cartId || cartLineKey(i)),
            );
          }
          setCartItems(items);
          const vendorId = items[0]?.vendorId?._id || items[0]?.vendorId;
          const controlsResponse = await get('/system-controls' + (vendorId ? '?vendorId=' + encodeURIComponent(vendorId) : ''));
          setSystemControls(controlsResponse.systemControls);
        } else {
          throw new Error(
            cartResponse.message || "Failed to fetch cart items.",
          );
        }

        // Fetch User Profile and Addresses
        if (user && user.role === "CUSTOMER") {
          const profileResponse = await get("/customers/me");
          if (profileResponse.success && profileResponse.customer) {
            setProfileUser(profileResponse.customer.userId || null);
            const customerAddresses = Array.isArray(
              profileResponse.customer.addresses,
            )
              ? profileResponse.customer.addresses
              : [];
            setAddresses(customerAddresses);
            if (customerAddresses.length > 0) {
              setSelectedAddress(current => customerAddresses.find(address => String(address._id) === String(current?._id || location.state?.selectedAddressId)) || customerAddresses.find(address => address.isDefault) || customerAddresses[0]);
            } else {
              setSelectedAddress(null);
            }
          } else {
            console.warn(
              "Failed to fetch customer profile:",
              profileResponse.message,
            );
          }
        }
      } catch (err) {
        console.error("Failed to fetch checkout data:", err);
        setError(
          err.response?.data?.message ||
            "An error occurred while loading checkout.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutData();
  }, [user, selectedIdsFromNav, cartReady, cartSyncError, pendingOrderIds.length, location.state?.selectedAddressId]);

  const storeGroups = React.useMemo(() => {
    const groups = new Map();
    cartItems.forEach((item) => {
      const vendor = item.vendorId;
      const key = String(vendor?._id || vendor || item.vendorName || 'store');
      if (!groups.has(key)) groups.set(key, { id: key, name: item.vendorName || vendor?.storeName || 'Store', logo: item.vendorLogo || vendor?.logo, items: [] });
      groups.get(key).items.push(item);
    });
    return [...groups.values()];
  }, [cartItems]);
  const selectedItemCount = cartItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const subtotal = cartItems.reduce(
    (acc, item) => acc + Math.round(Number(item.price) * 100) * item.quantity,
    0,
  ) / 100;
  const multipleStores = storeGroups.length > 1;
  const deliveryQuote = useDeliveryQuote(cartItems, selectedAddress?._id, orderType === 'DELIVERY' && !pendingOrderIds.length && !multipleStores, quoteRevision);
  const { quoteLoading, quoteError } = deliveryQuote;
  const deliveryFee = pendingOrderIds.length ? pendingDeliveryFee : deliveryQuote.deliveryFee;
  const total = subtotal + deliveryFee;
  const totalWeightGrams = cartItems.length && cartItems.every(item => Number.isFinite(Number(item.weightGrams)) && Number(item.weightGrams) > 0)
    ? cartItems.reduce((sum, item) => sum + Number(item.weightGrams) * Number(item.quantity), 0) : null;
  const deliveryRoutes = pendingOrderIds.length ? pendingRoutes : deliveryQuote.routes || [];

  const closeAddressDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddressIndex(null);
    setAddressError("");
  };

  const handleSaveAddress = async (address) => {
    setAddressError("");
    setAddressSaving(true);
    try {
      const response = editingAddressIndex === null
        ? await post("/customers/addresses", address)
        : await put(`/customers/addresses/${editingAddressIndex}`, address);
      if (!response.success || !response.customer || !Array.isArray(response.customer.addresses)) {
        setAddressError(response.message || "Failed to save address.");
        return false;
      }

      const updatedAddresses = response.customer.addresses;
      const savedAddress = editingAddressIndex === null
        ? updatedAddresses.find((candidate) => !addresses.some((existing) => String(existing._id) === String(candidate._id))) || updatedAddresses[updatedAddresses.length - 1]
        : updatedAddresses[editingAddressIndex];
      setAddresses(updatedAddresses);
      setQuoteRevision(current => current + 1);
      if (savedAddress) setSelectedAddress(savedAddress);
      setAddressDialogOpen(false);
      setEditingAddressIndex(null);
      return true;
    } catch (saveError) {
      console.error("Failed to save delivery address:", saveError);
      setAddressError(
        saveError.response?.data?.message ||
          saveError.message ||
          "Failed to save address. Please try again.",
      );
      return false;
    } finally {
      setAddressSaving(false);
    }
  };

  const checkoutUnavailable = multipleStores || !cartReady || Boolean(cartSyncError) || cartItems.some(item => item.available === false) || quoteLoading || Boolean(quoteError) || !systemControls || !orderType || systemControls.orderType?.[orderType] === false || !paymentOptions.some((o) => o.value === paymentMethod && !o.disabled);
  const handlePlaceOrder = async () => {
    if (placingOrder) return;
    if (multipleStores) { setError('Select items from one store in your cart before checkout.'); return; }
    if (checkoutUnavailable) { setError('No available payment method or order type. Please try again later.'); return; }
    if (!verificationReady) { setError(!accountReady ? (!onlinePayment ? 'Full account verification is required for cash payment. Complete the missing account details and checks.' : 'Add a mobile number before confirming checkout by SMS.') : 'Complete the checkout SMS confirmation below before continuing to payment.'); return; }
    if (!user) {
      setError("Please sign in before checkout.");
      return;
    }

    if (!currentUser?.phone || !String(currentUser.phone).trim()) {
      setError(
        "Please add a valid phone number to your profile before checkout.",
      );
      return;
    }

    if (orderType === "DELIVERY" && !selectedAddress) {
      setError("Please select a delivery address.");
      return;
    }

    if (cartItems.length === 0) {
      setError("No items selected for checkout.");
      return;
    }

    if (['COD', 'COP'].includes(paymentMethod) && !canUseCashOnDelivery) {
      setError(
        "Cash payment requires a fully verified account.",
      );
      return;
    }

    setPlacingOrder(true);
    setError("");
    try {
      const selectedItemsPayload = cartItems.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        expectedPrice: item.price,
        expectedQuantity: item.quantity,
      }));
      const orderPayload = {
        vendorId: checkoutVendorId,
        deliveryAddressId: selectedAddress?._id,
        paymentMethod,
        orderNote: deliveryNote.trim(),
        noteRecipient: orderType === 'PICKUP' ? 'VENDOR' : noteRecipient,
        deliveryNote: orderType === 'DELIVERY' && ['DELIVERY', 'BOTH'].includes(noteRecipient) ? deliveryNote.trim() : '',
        deliveryFee,
        orderType,
        selectedItems: selectedItemsPayload,
        expectedPricing: { subtotal, deliveryFee, total },
      };
      const response = pendingOrderIds.length
        ? { success: true, orders: pendingOrderIds.map((_id) => ({ _id })) }
        : await post("/orders", orderPayload);

      if (
        !response.success ||
        !Array.isArray(response.orders) ||
        response.orders.length === 0
      ) {
        if (response.success) {
          setError("Order was created but no order details were returned.");
          return;
        }

        setError(response.message || "Failed to place order.");
        return;
      }

      if (!['COD', 'COP'].includes(paymentMethod)) {
        const orderIds = response.orders.map((order) => order._id);
        setPendingOrderIds(orderIds);
        if (!pendingOrderIds.length) {
          setPendingDeliveryFee(response.orders.reduce((sum, order) => sum + Math.round(Number(order.pricing.deliveryFee || 0) * 100), 0) / 100);
          setPendingRoutes(response.orders.map(order => ({ ...order.deliveryRoute, vendorId: String(order.vendorId?._id || order.vendorId || order._id), storeName: cartItems.find(item => String(item.vendorId?._id || item.vendorId) === String(order.vendorId?._id || order.vendorId))?.vendorName, fee: Number(order.pricing.deliveryFee || 0) })));
          const savedLines = response.orders.flatMap(order => order.items);
          setCartItems(current => current.map(item => {
            const saved = savedLines.find(line => cartLineKey(line) === cartLineKey(item));
            return saved ? { ...item, price: saved.price, vendorPrice: saved.vendorPrice, platformMarkup: saved.platformMarkup, quantity: saved.qty, weightGrams: saved.weightGrams ?? item.weightGrams, weightNote: saved.weightNote ?? item.weightNote } : item;
          }));
        }
        const checkoutResponse = await post("/payments/checkout", {
          orderIds,
          paymentMethod,
          paymentProvider: systemControls.paymentProvider || 'PAYMONGO',
        });

        if (checkoutResponse?.success && checkoutResponse?.checkoutUrl) {
          window.location.assign(checkoutResponse.checkoutUrl);
          return;
        }

        setError(
          checkoutResponse?.message ||
            `${providerName} checkout could not be started.`,
        );
        return;
      }

      navigate("/orders");
    } catch (err) {
      if (err.response?.data?.code === 'CHECKOUT_SMS_REQUIRED' || err.response?.status === 403) setSmsRevision(value => value + 1);
      if (err.response?.data?.code === 'CHECKOUT_PRICE_CHANGED') {
        try {
          const updated = await get('/cart');
          setQuoteRevision(current => current + 1);
          const keys = new Set(cartItems.map(cartLineKey));
          setCartItems((updated.cart || []).filter(item => keys.has(cartLineKey(item))));
          setError('Prices changed. Review the updated total, then continue to payment.');
        } catch { setError('Prices changed. Reload checkout to review the updated total.'); }
        return;
      }
      console.error("Place order error:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while placing your order.",
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  if (cartSyncError) return <div role="alert">{cartSyncError} Please retry cart sync before checkout.</div>;
  if (loading || !cartReady) {
    return <div className="text-center py-8">Loading checkout...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      <header className="flex items-start gap-3">
        <Button type="button" variant="ghost" size="icon" aria-label="Go back" className="shrink-0" onClick={() => location.key === 'default' ? navigate('/cart') : navigate(-1)}>
          <ArrowLeft className="h-6 w-6" aria-hidden="true" />
        </Button>
        <div><h1 className="text-3xl font-bold">Checkout</h1><p className="mt-1 text-sm font-semibold text-gray-600">Order type: {orderType === 'PICKUP' ? 'Pick up' : 'Delivery'}</p></div>
      </header>
      {multipleStores && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        Checkout is limited to one store at a time. Return to your cart and select one store.
        <Button type="button" variant="outline" className="mt-2" onClick={() => navigate('/cart')}>Choose a store</Button>
      </div>}
      {new URLSearchParams(location.search).get('payment') === 'cancelled' && (
        <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          You returned from payment. Check your orders before starting again to avoid paying twice.
          {' '}<button type="button" className="font-semibold underline" onClick={() => navigate('/orders')}>View orders</button>
        </p>
      )}
      {!accountReady && (
        <div className="flex items-center justify-between gap-4 rounded-xl border bg-white p-4">
          <p className="min-w-0 text-sm text-gray-600">{!onlinePayment ? 'Full account verification is required for cash payment. Open account verification to see the missing details and checks.' : 'A mobile number is missing. Add your number before confirming checkout by SMS.'}</p>
          <Button className="shrink-0" onClick={() => navigate("/account-verification", { state: { returnTo: '/checkout', checkoutState: { selectedIds: selectedIdsFromNav, paymentMethod, orderType, deliveryNote, noteRecipient, selectedAddressId: selectedAddress?._id } } })}>Get verified</Button>
        </div>
      )}
      {accountReady && smsStatus?.context === checkoutLocation && smsStatus.required && <div className="space-y-3">
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{smsStatus.reason}</p>
        <ContactCode key={checkoutLocation} channel="phone" purpose="CHECKOUT" accountVerified={currentUser.verified === true} value={currentUser.phone} verified={false} editable={false}
          checkoutContext={{ orderType, deliveryAddressId: selectedAddress?._id }}
          onVerified={response => {
            setProfileUser(current => ({ ...(current || user), phone: response.phone || currentUser.phone, phoneVerifiedAt: response.phoneVerified ? new Date().toISOString() : currentUser.phoneVerifiedAt, ...(response.verified ? { verified: true } : {}) }));
            setError(''); setSmsRevision(value => value + 1);
          }} />
      </div>}
      {accountReady && !smsStatus && <p role="status" className="text-sm text-gray-500">Select a delivery location to check SMS verification.</p>}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {quoteError && <p role="alert" className="text-red-700">{quoteError}</p>}
      {quoteLoading && <p>Calculating delivery fee...</p>}
      {checkoutUnavailable && !quoteLoading && !quoteError && <p role="alert" className="text-amber-700">Checkout is unavailable with the current payment or order options. Please try again later.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <section aria-label="Selected items" className="space-y-3">
            {storeGroups.length === 0 ? <p className="text-sm text-gray-500">No items selected for checkout.</p> : storeGroups.map((group) => (
              <Card key={group.id}>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    {group.logo ? <img src={group.logo} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" /> : <Store size={20} className="shrink-0 text-primary" aria-hidden="true" />}
                    <span className="break-words">{group.name}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <ul className="divide-y">
                    {group.items.map((item) => (
                      <li key={item.cartId || cartLineKey(item)} className="flex items-start gap-3 py-2">
                        {item.imageUrl && <img src={item.imageUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />}
                        <div className="min-w-0 flex-1">
                          <p className="break-words text-sm font-medium text-gray-900">{item.name}</p>
                          <p className="mt-1 text-xs text-gray-500">Qty: {item.quantity} × ₱{Number(item.price || 0).toFixed(2)}</p>
                          {item.available === false && <p className="mt-1 text-xs text-red-600">{item.unavailableReason || 'This product is unavailable.'}</p>}
                        </div>
                        <p className="shrink-0 text-sm font-semibold text-gray-900">₱{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </section>
          <fieldset disabled={placingOrder || pendingOrderIds.length > 0} className="space-y-3">
          {/* Delivery Address */}
          {orderType === "DELIVERY" && (
            <>
            <Card>
              <CardHeader className="p-3 pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Delivery Address
                  </CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Add delivery address"
                    title="Add delivery address"
                    className="text-green-800 hover:bg-green-50 hover:text-green-900"
                    onClick={() => {
                      setEditingAddressIndex(null);
                      setAddressError("");
                      setAddressDialogOpen(true);
                    }}
                  >
                    <Plus className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {addresses.length > 0 ? (
                  addresses.map((addr, index) => (
                    <div
                      key={addr._id}
                      className={`mb-2 flex items-center justify-between gap-3 rounded-lg border p-3 ${
                        selectedAddress?._id === addr._id ? "border-primary bg-primary/5" : ""
                      }`}
                    >
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                        <input
                          type="radio"
                          name="deliveryAddress"
                          disabled={!addressMatchesStore(addr)}
                          value={addr._id}
                          checked={selectedAddress?._id === addr._id}
                          onChange={() => setSelectedAddress(addr)}
                          className="form-radio shrink-0 text-primary"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {deliveryAddressLabel(addr)}{addr.isDefault && <span className="ml-2 text-xs font-normal text-primary">Default</span>}
                            {!addr.location && <span className="ml-2 text-xs text-amber-700">Set a pin before delivery</span>}
                            <span className="ml-2 text-xs text-slate-500">{addr.branch?.name || addr.branchName || 'Outside platform areas'}</span>
                            {checkoutStore && !addressMatchesStore(addr) && <span className="ml-2 text-xs text-amber-700">Choose an address in {checkoutStore.branch?.name || 'the store area'}</span>}
                          </p>
                          <p className="break-words text-sm text-gray-500">
                            {deliveryAddressText(addr)}
                          </p>
                        </div>
                      </label>
                      <Button
                        disabled={addr.deliveryLocked}
                        title={addr.deliveryLocked ? 'Locked after successful delivery. Create a new address to make changes.' : 'Edit address'}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingAddressIndex(index);
                          setAddressError("");
                          setAddressDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No saved addresses yet. Add one to continue with delivery.
                  </p>
                )}
              </CardContent>
            </Card>
            <CustomerAddressDialog
              open={addressDialogOpen}
              address={editingAddressIndex === null ? null : addresses[editingAddressIndex]}
              saving={addressSaving}
              error={addressError}
              onClose={closeAddressDialog}
              onSave={handleSaveAddress}
            />
            </>
          )}

          </fieldset>
          {/* Payment Method */}
          <Card>
            <CardHeader className="p-3 pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Payment Method
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="space-y-3">
                {paymentOptions.length === 0 && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">No payment method is available for every selected store and this order type. Change the order type or check out stores separately.</p>}
                {paymentOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                      option.disabled
                        ? "bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={option.value}
                        checked={paymentMethod === option.value}
                        onChange={() =>
                          !option.disabled && setPaymentMethod(option.value)
                        }
                        className="form-radio text-primary"
                        disabled={option.disabled}
                      />
                      <span className="text-sm font-medium">
                        {option.label}
                      </span>
                    </div>
                    {option.disabled && (
                      <span className="text-[10px] uppercase tracking-wide text-gray-500">
                        unavailable
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {paymentMethod && !['COD', 'COP'].includes(paymentMethod) && (
                <div className="mt-3 flex gap-2 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-800">
                  <LockKeyhole className="h-4 w-4 shrink-0" />
                  <span>
                    You will complete payment securely on {providerName}. Your order
                    is confirmed only after payment is verified.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 px-3 py-2">
              <div><CardTitle className="text-base">Order note <span className="text-xs font-normal text-gray-500">(optional)</span></CardTitle>{deliveryNote.trim() && !noteOpen && <p className="mt-1 text-xs text-gray-500">Note added for {orderType === 'PICKUP' || noteRecipient === 'VENDOR' ? 'vendor' : noteRecipient === 'BOTH' ? 'vendor and delivery' : 'delivery'}</p>}</div>
              <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" aria-expanded={noteOpen} aria-controls="checkout-note-fields" onClick={() => setNoteOpen(open => !open)}>{noteOpen ? 'Hide note' : deliveryNote.trim() ? 'Edit Note' : 'Add Note'}</Button>
            </CardHeader>
            {noteOpen && <CardContent id="checkout-note-fields" className="px-3 pb-2 pt-0">
              <fieldset disabled={placingOrder || pendingOrderIds.length > 0} className="space-y-1.5">
                <legend className="mb-1 text-xs font-medium">Send note to</legend>
                <div className="flex flex-wrap gap-1.5">{(orderType === 'DELIVERY' ? [['DELIVERY', 'Delivery'], ['VENDOR', 'Vendor'], ['BOTH', 'Both']] : [['VENDOR', 'Vendor']]).map(([value, label]) => {
                  const selected = (orderType === 'PICKUP' ? 'VENDOR' : noteRecipient) === value;
                  return <label key={value} title={label} className={`flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2 transition focus-within:ring-2 focus-within:ring-primary/30 ${selected ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                    <input type="radio" name="noteRecipient" aria-label={label} value={value} checked={selected} onChange={() => setNoteRecipient(value)} className="h-4 w-4 accent-primary" />
                    {value !== 'DELIVERY' && <Store className="h-4 w-4" aria-hidden="true" />}{value !== 'VENDOR' && <Bike className="h-4 w-4" aria-hidden="true" />}
                  </label>;
                })}</div>
                <label htmlFor="deliveryNote" className="block text-xs font-medium">Your note</label>
                <textarea id="deliveryNote" rows={2} maxLength={1000} placeholder={orderType === 'PICKUP' || noteRecipient === 'VENDOR' ? 'e.g. Please pack the sauce separately.' : noteRecipient === 'BOTH' ? 'Instructions for the vendor and delivery rider.' : 'e.g. Please call when you arrive.'} className="min-h-[64px] w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm focus:border-green-600 focus:outline-none" value={deliveryNote} onChange={e => setDeliveryNote(e.target.value)} />
                <p className="text-xs text-gray-500">{deliveryNote.length}/1000 characters</p>
              </fieldset>
            </CardContent>}
          </Card>
        </div>

        {/* Order Summary Sticky on larger screens */}
        <div className="space-y-3">
          <Card role="region" aria-label="Order details" className="overflow-hidden border-slate-200 shadow-sm sm:sticky top-24">
            <CardHeader className="border-b border-slate-100 p-3">
              <div className="flex items-center gap-3">
                <span className="rounded-xl bg-primary/10 p-2.5 text-primary"><ShoppingBag className="h-5 w-5" aria-hidden="true" /></span>
                <div><CardTitle className="text-lg">Order Details</CardTitle><p className="mt-1 text-xs text-slate-500">Review your items and charges</p></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 p-3">
              <div className="grid grid-cols-2 divide-x divide-slate-200 rounded-xl bg-slate-50 py-2">
                <div className="flex flex-col gap-1 px-3"><span className="text-xs text-slate-500">Total items</span><span className="text-lg font-semibold tabular-nums text-slate-900">{selectedItemCount}</span></div>
                <div className="flex flex-col gap-1 px-3"><span className="text-xs text-slate-500">Total Weight</span><span className="break-words text-lg font-semibold tabular-nums text-slate-900">{formatWeight(totalWeightGrams)}</span></div>
              </div>
              <div className="flex items-center justify-between gap-3 text-sm"><span className="text-slate-600">Amount</span><span className="font-semibold tabular-nums text-slate-900">{'\u20B1' + subtotal.toFixed(2)}</span></div>
              {orderType === 'DELIVERY' && <div className="space-y-3 border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between gap-3"><h3 className="text-xs font-semibold text-slate-500">Delivery fee</h3><button type="button" onClick={() => setShowRates(true)} className="shrink-0 text-xs font-medium text-primary underline underline-offset-4 hover:no-underline">See rates</button></div>
                {deliveryRoutes.length > 0 ? deliveryRoutes.map(route => <div key={route.vendorId} className="space-y-2 rounded-xl border border-slate-200 p-3">
                  <h3 className="break-words text-sm font-medium text-slate-800">{route.tierName || 'Delivery charge'}</h3>
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1 text-xs text-slate-500">{Number.isFinite(route.distanceKm) && <><MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span className="break-words">{`${deliveryAddressLabel(selectedAddress)} | ${route.distanceKm.toFixed(2)} km by road`}</span></>}</span>
                    <span className="shrink-0 text-right text-sm font-semibold tabular-nums text-slate-900">{'\u20B1' + Number(route.fee || 0).toFixed(2)}</span>
                  </div>
                  {deliveryRoutes.length > 1 && <p className="text-xs text-slate-500">{route.storeName || 'Store'}</p>}
                </div>) : <div className="flex justify-between gap-3 rounded-xl border border-slate-200 p-3 text-sm"><span className="text-slate-600">Delivery charge</span><span className="text-right font-medium">{quoteLoading ? 'Calculating...' : quoteError ? 'Unavailable' : '\u20B1' + deliveryFee.toFixed(2)}</span></div>}
              </div>}
              <div className="flex items-center justify-between gap-3 rounded-xl bg-primary/5 px-3 py-3">
                <span className="font-semibold text-slate-900">Total</span>
                <span className="text-2xl font-bold tracking-tight tabular-nums text-primary">₱{total.toFixed(2)}</span>
              </div>
              <Button
                className="w-full rounded-xl py-3 text-sm sm:text-base"
                size="lg"
                onClick={handlePlaceOrder}
                disabled={
                  placingOrder || checkoutUnavailable || !verificationReady ||
                  (orderType === "DELIVERY" && !selectedAddress) ||
                  cartItems.length === 0
                }
              >
                {placingOrder
                  ? ['COD', 'COP'].includes(paymentMethod)
                    ? "Placing order..."
                    : "Opening secure payment..."
                  : ['COD', 'COP'].includes(paymentMethod)
                    ? "Place order"
                    : "Continue to secure payment"}
              </Button>
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <ShoppingBag className="h-3.5 w-3.5" />
                <span>
                  {paymentMethod === "COD"
                    ? "Pay when your order arrives"
                    : paymentMethod === "COP"
                      ? "Pay when you pick up your order"
                    : `Powered securely by ${providerName}`}
                </span>
              </div>
              <p className="text-[10px] text-center text-gray-400 mt-2">
                By clicking "Place Order", you agree to our Terms of Service and
                Privacy Policy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      {showRates && <DeliveryRatesDialog vendorId={checkoutVendorId} onClose={() => setShowRates(false)} />}
    </div>
  );
};

export default Checkout;
